"use client";

import { useState } from "react";
import type { AuthUser, PrivacyLevel, UpdateProfileInput } from "@podlog/shared";
import { updateProfile, uploadAvatar } from "../lib/api";
import { useAuth } from "./AuthProvider";
import Avatar from "./Avatar";

/** The editable profile-settings form body (avatar + fields). Shared by the
 *  settings page and the settings modal overlay. */
export default function ProfileSettingsForm({ user }: { user: AuthUser }) {
  const { refresh } = useAuth();
  const [displayName, setDisplayName] = useState(user.display_name ?? "");
  const [username, setUsername] = useState(user.username ?? "");
  const [bio, setBio] = useState(user.bio ?? "");
  const [privacy, setPrivacy] = useState<PrivacyLevel>(user.privacy ?? "public");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(user.avatar_url);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  async function handleAvatar(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file later
    if (!file) return;
    setAvatarError(null);
    setAvatarBusy(true);
    try {
      const updated = await uploadAvatar(file);
      setAvatarUrl(updated.avatar_url);
      await refresh(); // so the menu/profile pick up the new photo
    } catch (err) {
      setAvatarError(err instanceof Error ? err.message : "Avatar upload failed.");
    } finally {
      setAvatarBusy(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaved(false);
    setBusy(true);
    try {
      const payload: UpdateProfileInput = {
        displayName: displayName.trim() === "" ? null : displayName.trim(),
        bio: bio.trim() === "" ? null : bio.trim(),
        privacy,
      };
      if (username.trim() !== "") payload.username = username.trim();
      await updateProfile(payload);
      await refresh();
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Update failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <Avatar url={avatarUrl} name={displayName || username} size={64} />
        <div className="flex flex-col gap-1">
          <label className="cursor-pointer text-xs font-medium text-sky-300 hover:underline">
            {avatarBusy ? "Uploading…" : "Change photo"}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleAvatar}
              disabled={avatarBusy}
            />
          </label>
          <span className="text-[10px] text-sky-200/50">JPG/PNG, max 2MB.</span>
          {avatarError && <span className="text-xs text-red-400">{avatarError}</span>}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-xs text-sky-200/70">
          Display name
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Display name"
            className="rounded-md border border-sky-900/60 bg-ocean-800 px-3 py-2 text-sm text-foam"
          />
        </label>

        <label className="flex flex-col gap-1 text-xs text-sky-200/70">
          Username
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Username"
            className="rounded-md border border-sky-900/60 bg-ocean-800 px-3 py-2 text-sm text-foam"
          />
        </label>

        <label className="flex flex-col gap-1 text-xs text-sky-200/70">
          Bio
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="Bio"
            rows={4}
            className="rounded-md border border-sky-900/60 bg-ocean-800 px-3 py-2 text-sm text-foam"
          />
        </label>

        <label className="flex flex-col gap-1 text-xs text-sky-200/70">
          Privacy
          <select
            value={privacy}
            onChange={(e) => setPrivacy(e.target.value as PrivacyLevel)}
            className="rounded-md border border-sky-900/60 bg-ocean-800 px-3 py-2 text-sm text-foam"
          >
            <option value="public" className="bg-ocean-900 text-foam">Public</option>
            <option value="friends_only" className="bg-ocean-900 text-foam">Friends only</option>
            <option value="private" className="bg-ocean-900 text-foam">Private</option>
          </select>
        </label>

        <button
          type="submit"
          disabled={busy}
          className="rounded-md bg-orca px-3 py-2 text-sm font-semibold text-ocean-950 hover:bg-sky-300 disabled:opacity-50"
        >
          {busy ? "Saving…" : "Save"}
        </button>

        {saved && !error && <p className="text-xs text-dolphin">Saved.</p>}
        {error && <p className="text-xs text-red-400">{error}</p>}
      </form>
    </div>
  );
}
